import React from 'react';
import { useParams } from 'react-router-dom';

import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';

import { Loading } from '@common/react/components/UI/Loading/Loading';
import { phoneRegexp } from '@common/react/utils/validationHelpers';

import { rest } from '@app/components/Api';
import CustomCheckbox from '@app/components/Forms/CustomCheckbox/CustomCheckbox';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import { CustomPhoneControl } from '@app/components/Forms/CustomPhoneControl/CustomPhoneControl';
import LinkMore from '@app/components/LocalCommon/LinkMore/LinkMore';
import { stringBeautifyValidator } from '@app/components/LocalCommon/Utils/Validators';
import { eventToLink } from '@app/components/LocalCommon/Utils/eventToLink';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import DiseaseCard from '@app/components/UI/DiseaseCard/DiseaseCard';
import FiftyFiftyExtend from '@app/components/UI/FiftyFifty/FyftyFiftyExtend';
import { Resource } from '@app/objects/Resource';
import '@app/scss/components/QOLResults.scss';

enum PetState {
	Ok,
	NeedHelp,
}

const answerType = {
	0: 'Agree',
	1: 'Somewhat agree',
	2: 'Disagree',
};

interface PetScore {
	maxScore: number;
	name: string;
	petType: string;
	petState: PetState;
	score: number;
	relatedArticles: Resource[];
	dotScores: [];
	answers: any;
}

interface Answer {
	text: string;
}

const validationSchema = Yup.object({
	firstName: stringBeautifyValidator('First Name is a required field'),
	lastName: stringBeautifyValidator('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	phone: Yup.string().required('Phone Number is a required field').matches(phoneRegexp, 'Invalid Phone Number'),
	zip: stringBeautifyValidator('ZipCode is a required field'),
});

const QOLResults = (props) => {
	const [petScore, setPetScore] = React.useState<PetScore>({
		maxScore: 0,
		name: '',
		petType: 'dog',
		petState: PetState.Ok,
		score: 0,
		relatedArticles: [],
		dotScores: [],
		answers: [],
	});
	const [petType, setPetType] = React.useState('');
	const [petName, setPetName] = React.useState<string>('');
	const [sessionId, setSessionId] = React.useState<string>();
	const [messageSuccess, setMessageSuccess] = React.useState<boolean>(false);
	const [isChecked, setIsChecked] = React.useState<boolean>(false);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const params: { id?: string | undefined } = useParams();

	React.useEffect(() => {
		if (!params.id) {
			setPetType(props.location.state.petType);
			setPetName(props.location.state.petName);

			rest.post<PetScore>('v1/qol/finish', {
				sessionId: props.location.state.sessionId,
				answers: props.location.state.answerList,
			}).then((res) => {
				setPetScore({ ...petScore, ...res });
				setSessionId(props.location.state.sessionId);
			});
		} else {
			rest.get<PetScore>(`v1/qol/results?sessionId=${params.id}`).then((res) => {
				setPetScore({ ...petScore, ...res });
				setPetType(res.petType);
				setPetName(res.name);
				setSessionId(params.id);
			});
		}
	}, [params]);

	const commonText =
		'When evaluating the quality of life of your pet, personalized patient and family information play a substantial role in making an educated, informed, and supported decision that suits not only your pet’s medical condition, but also your wishes and expectations. In short, quality of life applies not only to the pet, it also applies to you!';

	const getPersonalText = (petScore: PetScore) => {
		if (!petScore || !petScore.name) {
			return '';
		}

		if (petScore.score >= 1 && petScore.score <= 4) {
			return `${petScore.name}'s quality of life appears to be okay. Monitoring your pet may be all that is needed at this time. Write down the symptoms you are concerned about and keep track of any changes or worsening in them. Discuss these changes with your regular veterinarian on your next semi-annual exam or possibly schedule a visit with them sooner. They can also help you identify signs to look for in the future as well as uncover subtle signs that you may have missed.`;
		}

		if (petScore.score >= 5 && petScore.score <= 7) {
			return `${petScore.name}'s quality of life is questionable. Scheduling a visit with your regular veterinarian to evaluate the symptoms they are experiencing would be recommended. Your pet would benefit from veterinary oversight and possibly from medical intervention. To speak with a Lap of Love hospice doctor about the disease process your pet is experiencing, contact us to schedule a telehospice appointment.`;
		}

		if (petScore.score >= 8 && petScore.score <= 10) {
			return `${petScore.name}'s quality of life seems to be declining. Scheduling a visit with your regular veterinarian to evaluate the symptoms they are experiencing would be recommended as your pet would benefit from veterinary oversight and possibly from medical intervention. Lap of Love may be able to assist you with an in-home evaluation if we service your area or we can provide a telehospice appointment anywhere around the country.`;
		}

		if (petScore.score >= 11) {
			return `${petScore.name}'s quality of life is quite concerning. It is likely that changes will continue to develop and become more severe in the near future. Seeking guidance from your regular veterinarian on the progression of these symptoms will help you better understand the end stages of the disease process that your pet is experiencing. For guidance on determining the best next step and when to consider euthanasia, you can schedule a telehospice consultation with one of our Lap of Love hospice doctors. If we service your area, we can also provide a hospice evaluation in your home.`;
		}

		return `${petScore.name}'s quality of life appears to be really good! We suggest bi-annual exams with your regular veterinarian when a pet reaches their senior years as they can help you identify signs to look for in the future and uncover anything that may be missed at this time.`;
	};

	const getResultsForm = React.createRef<HTMLDivElement>();
	const scrollToForm = () => getResultsForm.current?.scrollIntoView({ behavior: 'smooth' });

	if (!petScore || !petScore.name) {
		return <Loading />;
	}
	return (
		<div className="qol-results__wrapper">
			<div className="qol-results">
				<div className="qol-results-body">
					<FiftyFiftyExtend
						title={`${petName}'s results`}
						evaluation={`Overall score ${petScore.score}/${petScore.maxScore}`}
						text={getPersonalText(petScore)}
						commonText={commonText}
						src={
							petType && petType === 'dog'
								? require('@app/content/qualityOfLife/dog.svg')
								: require('@app/content/qualityOfLife/cat.svg')
						}
						useGetInTouchButton={params.id ? params.id : null}
						btnText="Get a copy of your score"
						btnClick={scrollToForm}
						name={petType && petType === 'dog' ? 'qol-results-dog' : 'qol-results-cat'}
						// @ts-ignore
						dotScores={petScore.dotScores ? petScore.dotScores : []}
					/>
					<div className="qol-results__resources-wrapper">
						<div className="qol-results__resources">
							<h6>Helpful resources</h6>
							<h2>
								Informational resources <br /> to help you <span className="highlight">navigate</span>
								<br /> your pet's quality of life
							</h2>
							<LinkMore
								linkMoreCaption="Explore all of our quality of life resources"
								linkMoreLink="/how-will-i-know-it-is-time"
								target="_blank"
							/>
							<LinkMore
								linkMoreCaption="Download our Quality of Life Daily Assessment to track your pet's progress"
								linkMoreLink="/how-will-i-know-it-is-time/interactive-quality-of-life-assessment-and-diary.pdf"
								target="_blank"
								handleCLick={() => eventToLink("Download our Quality of Life Daily Assessment to track your pet's progress")}
							/>
						</div>

						{petScore.relatedArticles.length > 0 && (
							<div className="qol-results__resources-list">
								{petScore &&
									petScore.relatedArticles.map((item, index) => {
										return (
											<DiseaseCard
												key={item.id}
												name={item.title}
												shortText={item.shortText}
												url={`/resource-center/${item.topic.url}/${item.url}`}
												topic={item.topic.name}
											/>
										);
									})}
							</div>
						)}
					</div>
					<div className="find-vet bottom-form" ref={getResultsForm}>
						{params.id && (
							<div className="qol-results__answers">
								{petScore.answers.map((item) => {
									return (
										<div className="qol-results__answers__item" key={`question-${item.id}`}>
											<p className="question">{item.question.text}</p>
											<p className="answer">{answerType[item.answerType]}</p>
										</div>
									);
								})}
							</div>
						)}
						{!params.id && (
							<div className="find-vet-left bottom-form__left">
								{!messageSuccess ? (
									<>
										<h3 className="find-vet-left__title">Get a copy of {petScore && petScore.name}'s quality of life score</h3>
										<Formik
											onSubmit={(values) => {
												values.qolSessionId = sessionId;
												values.teleadvice = isChecked;

												setIsLoading(true);

												rest.post('v1/qol/email', values)
													.then((res) => {
														setMessageSuccess(true);
													})
													.finally(() => {
														setIsLoading(false);
													});
											}}
											initialValues={{
												firstName: '',
												lastName: '',
												email: '',
												phone: '',
												zip: '',
												teleadvice: isChecked,
												qolSessionId: sessionId,
											}}
											validationSchema={validationSchema}
											render={(formikBag: FormikProps<any>) => {
												return (
													<Form className="qol-results-form">
														<CustomInput type="text" name="firstName" placeholder="First Name*" formikBag={formikBag} />
														<CustomInput type="text" name="lastName" placeholder="Last Name*" formikBag={formikBag} />
														<CustomInput type="email" name="email" placeholder="Email Address*" formikBag={formikBag} />

														<div className="phone">
															<FormikField
																title="Phone Number"
																fieldName="phone"
																render={fieldProps => (
																	<CustomPhoneControl placeholder="Phone Number*" fieldProps={fieldProps} />
																)}
															/>
														</div>
														<CustomInput type="zip" name="zip" placeholder="ZipCode*" formikBag={formikBag} />
														<div className="qol-results-form__checkbox">
															<CustomCheckbox
																isChecked={isChecked}
																setIsChecked={setIsChecked}
																formikBag={formikBag}
																capture="Would you like a telehospice session with a hospice veterinarian to discuss your pet's quality of life?"
															/>
														</div>
														<div className="text">
															By providing a telephone number and submitting the form you are consenting to be contacted by SMS text message by Lap of Love.
															Message data rates may apply. Reply STOP to opt out of further messaging.
														</div>
														<div className="qol-results-form__btn">
															<button
																type="submit"
																className="btn btn-primary"
																disabled={isLoading}
															>
																Submit
															</button>
															{isLoading ? (
																<div className="lds-ring">
																	<div />
																	<div />
																	<div />
																	<div />
																</div>
															) : null}
														</div>
													</Form>
												);
											}}
										/>
									</>
								) : (
									<div>
										<p className="find-vet-left__title">Thank you! You will receive an email with your results soon.</p>
									</div>
								)}
							</div>
						)}
						<div className="find-vet-right">
							<h4 className="find-vet-right__title">Call us to schedule a telehospice consultation with one of our doctors.</h4>
							<a className="find-vet-right__phone" href="tel:855-933-5683">
								<img className="find-vet-right__phone-icon" src={require('@app/images/call_icon_white.svg')} alt="Phone Icon" />
								<img
									className="find-vet-right__phone-icon find-vet-right__phone-icon_hovered"
									src={require('@app/images/call_icon_white-filled.svg')}
									alt="Phone Icon"
								/>
								<span>(855) 933-5683</span>
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default QOLResults;
